// CONTRACT:I4-POLICY-DECISION.1.0
/** personas.ts — the INFORMAL identity metadata layer (the formal/informal seam).
 *
 *  The sealed policy bundle (defense.local) holds ONLY FORMAL ABAC attributes +
 *  identity clearance claims. The INFORMAL concepts a human reader wants — a real
 *  displayName, a job role, an org department — are NOT markings and MUST NEVER be
 *  treated as ABAC (that was mis-mapping M6: `role`/`department` masquerading as
 *  policy attrs). They live HERE instead, keyed by username, and the COP reads them
 *  ONLY for display / audit legibility (never for an access decision).
 *
 *  Source of truth: infra/policy/gen-canonical-policy.py emits both the sealed bundle
 *  AND apps/cop/public/personas.json from the same TDFBot roster; this module is the
 *  typed in-bundle mirror of that JSON so COP code can import it without a fetch.
 *  Regenerate per infra/policy/README.md. See docs/AOA-CLASSIFICATION-IDENTITY-MODEL.md
 *  §10 ("Formal vs informal STORAGE — the clean seam"). */

/** One persona's INFORMAL metadata. NONE of these fields is an ABAC attribute. */
export interface PersonaMeta {
  /** Human display name, e.g. "Sarah Chen" (shown instead of the bare username). */
  displayName: string;
  /** Job role (informal — the old `role` attr, now metadata), e.g. "Intelligence". */
  role: string;
  /** Org unit (informal — the old `department` attr, now metadata), e.g. "J2". */
  department: string;
}

/** username → informal metadata. Mirrors apps/cop/public/personas.json verbatim. */
export const PERSONAS: Record<string, PersonaMeta> = {
  "william.hartley": { displayName: "William Hartley", role: "Communications", department: "J6" },
  "sarah.chen": { displayName: "Sarah Chen", role: "Intelligence", department: "J2" },
  "james.rodriguez": { displayName: "James Rodriguez", role: "Intelligence", department: "J2" },
  "amara.okafor": { displayName: "Amara Okafor", role: "Intelligence", department: "J2" },
  "michael.petrov": { displayName: "Michael Petrov", role: "Intelligence", department: "J2" },
  "diana.price": { displayName: "Diana Price", role: "Operations", department: "J3" },
  "raj.patel": { displayName: "Raj Patel", role: "Communications", department: "J6" },
  "elena.vasquez": { displayName: "Elena Vasquez", role: "Intelligence", department: "J2" },
  "tom.nakamura": { displayName: "Tom Nakamura", role: "Operations", department: "J3" },
  "lisa.fairbanks": { displayName: "Lisa Fairbanks", role: "Other Gov Agency", department: "OGA" },
  "alex.wright": { displayName: "Alex Wright", role: "Intelligence", department: "J2" },
  "maria.santos": { displayName: "Maria Santos", role: "Operations", department: "J3" },
  "brian.lee": { displayName: "Brian Lee", role: "Communications", department: "J6" },
  "karen.mitchell": { displayName: "Karen Mitchell", role: "Logistics", department: "J4" },
  "david.park": { displayName: "David Park", role: "Communications", department: "J6" },
  "sophie.taylor": { displayName: "Sophie Taylor", role: "Intelligence", department: "J2" },
  "marcus.johnson": { displayName: "Marcus Johnson", role: "Personnel", department: "J1" },
  "jessica.brown": { displayName: "Jessica Brown", role: "Logistics", department: "J4" },
  "kevin.murphy": { displayName: "Kevin Murphy", role: "Communications", department: "J6" },
  "rachel.green": { displayName: "Rachel Green", role: "Public Affairs", department: "PA" },
  "contractor.demo": { displayName: "Contractor Demo", role: "Communications", department: "J6" },
  "visitor.demo": { displayName: "Visitor Demo", role: "Service", department: "SYS" },
  "nigel.ashworth": { displayName: "Nigel Ashworth", role: "Intelligence", department: "J2" },
  "emma.clarke": { displayName: "Emma Clarke", role: "Intelligence", department: "J2" },
  "luc.tremblay": { displayName: "Luc Tremblay", role: "Operations", department: "J3" },
  "tak-gateway": { displayName: "TAK Gateway (service)", role: "NPE / service", department: "SYS" },
};

/** The informal metadata for a username, or undefined if unknown (the caller falls
 *  back to the raw username — informal metadata is never load-bearing). */
export function personaFor(username: string | undefined | null): PersonaMeta | undefined {
  if (!username) return undefined;
  return PERSONAS[username];
}

/** The human display name for a username — the real name when known, else the bare
 *  username unchanged. Display-only; never a security boundary. */
export function displayNameFor(username: string | undefined | null): string {
  const u = (username ?? "").trim();
  return PERSONAS[u]?.displayName ?? u;
}
